<template>
  <UModal
    v-model:open="isOpen"
    :title="modalTitle"
    :description="modalDescription"
    :ui="{
      content: 'sm:max-w-6xl w-full'
    }"
  >
    <template #title>
      <div class="flex items-center gap-3 flex-wrap">
        <div class="flex items-center gap-2">
          <UIcon name="i-heroicons-share" class="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <span class="font-bold text-gray-900 dark:text-white">Workflow Blueprint: {{ blueprintTitle }}</span>
          <UBadge v-if="blueprintVersionDisplay" color="neutral" variant="subtle" size="xs">
            v{{ blueprintVersionDisplay }}
          </UBadge>
        </div>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-3">
        <!-- View mode switcher: Graphisch vs. YAML -->
        <div class="flex items-center justify-between border-b border-default pb-3">
          <div class="flex gap-2">
            <UButton
              :variant="viewMode === 'graphical' ? 'solid' : 'ghost'"
              :color="viewMode === 'graphical' ? 'primary' : 'neutral'"
              size="xs"
              icon="i-heroicons-rectangle-group"
              label="Graphisch"
              @click="viewMode = 'graphical'"
            />
            <UButton
              :variant="viewMode === 'yaml' ? 'solid' : 'ghost'"
              :color="viewMode === 'yaml' ? 'primary' : 'neutral'"
              size="xs"
              icon="i-heroicons-code-bracket"
              label="YAML"
              @click="viewMode = 'yaml'"
            />
          </div>
          <div v-if="viewMode === 'yaml'" class="flex items-center gap-2">
            <UButton
              color="neutral"
              variant="outline"
              size="xs"
              icon="i-heroicons-clipboard-document"
              label="Kopieren"
              @click="copyYaml"
            />
          </div>
        </div>

        <!-- Content Area -->
        <div class="h-[70vh] w-full p-1 border border-default rounded-lg bg-gray-50/50 dark:bg-gray-900/50 overflow-hidden relative">
          <div v-if="loading" class="flex flex-col items-center justify-center h-full gap-4">
            <UIcon name="i-heroicons-arrow-path" class="w-8 h-8 animate-spin text-gray-400" />
            <p class="text-sm text-muted">Blueprint wird geladen...</p>
          </div>
          <div v-else-if="error" class="flex flex-col items-center justify-center h-full gap-2">
            <UIcon name="i-heroicons-exclamation-triangle" class="w-8 h-8 text-danger" />
            <p class="text-sm text-danger">{{ error }}</p>
          </div>
          <div v-else-if="!currentBlueprint" class="flex flex-col items-center justify-center h-full gap-2">
            <UIcon name="i-heroicons-information-circle" class="w-8 h-8 text-muted" />
            <p class="text-sm text-muted">Kein Blueprint vorhanden.</p>
          </div>
          <template v-else>
            <!-- Graphical View -->
            <div v-show="viewMode === 'graphical'" class="h-full w-full">
              <WorkflowCanvas
                :blueprint="currentBlueprint"
                read-only
              />
            </div>
            <!-- YAML View -->
            <div v-show="viewMode === 'yaml'" class="h-full w-full overflow-hidden rounded-md">
              <Codemirror
                v-model="blueprintYaml"
                :disabled="true"
                :extensions="extensions"
                :indent-with-tab="true"
                :style="{ height: '100%', fontSize: '13px' }"
                :tab-size="2"
                class="h-full w-full font-mono"
              />
            </div>
          </template>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <UButton
          color="neutral"
          variant="ghost"
          label="Schließen"
          @click="isOpen = false"
        />
      </div>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import * as yaml from 'js-yaml'
import { Codemirror } from 'vue-codemirror'
import { yaml as yamlLang } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import WorkflowCanvas from '@/components/workflow/WorkflowCanvas.vue'
import type { WorkflowBlueprint } from '@/types'

const props = defineProps<{
  open: boolean
  blueprint?: WorkflowBlueprint | null
  instanceId?: string | null
  blueprintId?: string | null
  blueprintVersion?: number | string | null
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit('update:open', val)
})

const currentBlueprint = ref<WorkflowBlueprint | null>(props.blueprint || null)
const loading = ref(false)
const error = ref<string | null>(null)
const viewMode = ref<'graphical' | 'yaml'>('graphical')

const toast = useToast()
const extensions = [yamlLang(), oneDark]

const blueprintTitle = computed(() => {
  return currentBlueprint.value?.id || props.blueprintId || (loading.value ? 'Wird geladen...' : '-')
})

const blueprintVersionDisplay = computed(() => {
  return currentBlueprint.value?.version || props.blueprintVersion || null
})

const modalTitle = computed(() => {
  return `Workflow Blueprint: ${blueprintTitle.value}`
})

const modalDescription = computed(() => {
  const v = blueprintVersionDisplay.value ? ` (v${blueprintVersionDisplay.value})` : ''
  return `Ansicht des zugrundeliegenden Blueprints für diese Workflow-Instanz${v}`
})

const blueprintYaml = computed(() => {
  if (!currentBlueprint.value) return ''
  try {
    const cleanBlueprint = JSON.parse(JSON.stringify(currentBlueprint.value))
    if (cleanBlueprint.steps && Array.isArray(cleanBlueprint.steps)) {
      cleanBlueprint.steps.forEach((step: Record<string, unknown>) => {
        delete step.position
        delete step.style
      })
    }
    return yaml.dump(cleanBlueprint, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      skipInvalid: true,
    })
  } catch (e) {
    console.error('Fehler bei der YAML-Erzeugung:', e)
    return '# Fehler bei der YAML-Generierung'
  }
})

const fetchBlueprint = async () => {
  if (props.blueprint) {
    currentBlueprint.value = props.blueprint
    return
  }
  if (!props.instanceId && !props.blueprintId) return

  loading.value = true
  error.value = null
  try {
    const url = props.instanceId
      ? `/api/workflow/v1/tasks/${props.instanceId}/blueprint`
      : `/api/workflow/v1/blueprints/${props.blueprintId}`
    const res = await axios.get(url)
    currentBlueprint.value = res.data
  } catch (err) {
    console.error('Fehler beim Laden des Blueprints:', err)
    // If instance-specific blueprint not found, fallback to loading by blueprintId
    if (props.instanceId && props.blueprintId) {
      try {
        const fallbackRes = await axios.get(`/api/workflow/v1/blueprints/${props.blueprintId}`)
        currentBlueprint.value = fallbackRes.data
        return
      } catch (fallbackErr) {
        console.error('Fallback auf Blueprint fehlgeschlagen:', fallbackErr)
      }
    }
    error.value = 'Blueprint konnte nicht geladen werden.'
  } finally {
    loading.value = false
  }
}

const copyYaml = async () => {
  if (!blueprintYaml.value) return
  try {
    await navigator.clipboard.writeText(blueprintYaml.value)
    toast.add({
      title: 'Kopiert',
      description: 'YAML in die Zwischenablage kopiert',
      color: 'success',
    })
  } catch (e) {
    console.error('Kopieren fehlgeschlagen', e)
  }
}

watch(
  () => props.open,
  (val) => {
    if (val) {
      viewMode.value = 'graphical'
      if (props.blueprint) {
        currentBlueprint.value = props.blueprint
      } else {
        fetchBlueprint()
      }
    } else {
      if (!props.blueprint) {
        currentBlueprint.value = null
      }
    }
  },
  { immediate: true }
)

watch(
  () => props.blueprint,
  (newBp) => {
    if (newBp) {
      currentBlueprint.value = newBp
    }
  }
)
</script>
